
/**
 * Central coordinator for managing animation sequences and state transitions
 * to prevent race conditions between components
 */

import { timeoutManager } from "./TimeoutManager";

export type AnimationStage = 'idle' | 'fadeOut' | 'revealing' | 'revealed' | 'completed';

export interface AnimationState {
  id: string;
  stage: AnimationStage;
  wordLength: number;
  startTime: number;
  completionCallback?: () => void;
  revealedLetters: boolean[];
}

class AnimationCoordinator {
  private animations: Map<string, AnimationState> = new Map();
  
  /**
   * Register a new animation sequence
   * @param id Unique identifier for the animation
   * @param wordLength Length of the word being animated
   * @param completionCallback Function to call when animation completes
   */
  registerAnimation(
    id: string, 
    wordLength: number, 
    completionCallback?: () => void
  ): AnimationState {
    // Clear any existing animation with this ID
    if (this.animations.has(id)) {
      this.cancelAnimation(id);
    }
    
    const animationState: AnimationState = {
      id,
      stage: 'idle',
      wordLength,
      startTime: Date.now(),
      completionCallback,
      revealedLetters: Array(wordLength).fill(false)
    };
    
    this.animations.set(id, animationState);
    console.log(`🎭 Animation registered: ${id}`, animationState);
    
    return animationState;
  }
  
  /**
   * Get the current state of an animation
   * @param id Animation identifier
   */
  getAnimationState(id: string): AnimationState | undefined {
    return this.animations.get(id);
  }
  
  /**
   * Update the stage of an animation and trigger related actions
   * @param id Animation identifier
   * @param stage New animation stage
   */
  updateStage(id: string, stage: AnimationStage): boolean {
    const animation = this.animations.get(id);
    if (!animation) return false;
    
    const prevStage = animation.stage;
    animation.stage = stage;
    
    console.log(`🎭 Animation ${id}: ${prevStage} -> ${stage}`, {
      animation,
      timestamp: new Date().toISOString()
    });
    
    // Handle stage-specific logic
    if (stage === 'completed' && animation.completionCallback) {
      // Execute completion callback on next tick to avoid cascading state updates
      setTimeout(() => {
        if (animation.completionCallback) {
          console.log(`🎭 Animation ${id}: Executing completion callback`);
          animation.completionCallback();
        }
      }, 0);
    }
    
    return true;
  }
  
  /**
   * Update the revealed letters state for letter-by-letter animations
   * @param id Animation identifier
   * @param letterIndex Index of the letter being revealed
   */
  revealLetter(id: string, letterIndex: number): boolean {
    const animation = this.animations.get(id);
    if (!animation || letterIndex >= animation.wordLength) return false;
    
    animation.revealedLetters[letterIndex] = true;
    
    // Check if all letters are revealed
    if (animation.revealedLetters.every(revealed => revealed)) {
      console.log(`🎭 Animation ${id}: All letters revealed, transitioning to revealed stage`);
      this.updateStage(id, 'revealed');
      
      // Short delay before marking as completed
      timeoutManager.setTimeout(`animation-${id}-complete`, () => {
        this.updateStage(id, 'completed');
      }, 400);
    }
    
    return true;
  }
  
  /**
   * Cancel an animation and clean up resources
   * @param id Animation identifier
   */
  cancelAnimation(id: string): boolean {
    const animation = this.animations.get(id);
    if (!animation) return false;
    
    timeoutManager.clearTimeout(`animation-${id}-complete`);
    this.animations.delete(id);
    
    console.log(`🎭 Animation ${id}: Cancelled`);
    return true;
  }
  
  /**
   * Clear all animations and reset the coordinator
   */
  reset(): void {
    for (const [id] of this.animations) {
      this.cancelAnimation(id);
    }
    console.log('🎭 Animation coordinator reset');
  }
}

// Create singleton instance
export const animationCoordinator = new AnimationCoordinator();
